// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * A first-party decoder that yields code points together with the byte span each one occupies.
 *
 * WHY NOT `TextDecoder`. Two reasons, and both of them are the difference between a tool you can
 * trust with a file and one you cannot:
 *
 *  1. `TextDecoder` silently replaces every invalid byte with U+FFFD. Real files — a UTF-8 document
 *     with one Latin-1 byte in it from a decade-old CMS — then decode to something that is not what
 *     is on disk, and any "cleaned" file written from that string quietly corrupts bytes charsight
 *     was never asked to touch. Here an invalid byte is reported as an invalid byte, at its exact
 *     offset, and is passed through untouched.
 *
 *  2. JavaScript strings are UTF-16. `"𝐀".length` is 2, `"𝐀".charCodeAt(0)` is a surrogate, and a
 *     detector that works in string indices will place its highlight — and its splice — in the
 *     wrong place the moment an emoji appears earlier in the line. Deriving a byte offset from a
 *     string index after the fact is where this class of tool goes wrong, so both numbers are
 *     produced in the same pass and travel together from here on.
 */

export type Encoding = 'utf-8' | 'utf-16le' | 'utf-16be';

export interface Unit {
  /** The decoded code point, or -1 for a byte that is not part of a valid sequence. */
  cp: number;
  /** Byte offset of the first byte of this unit within the original buffer. */
  offset: number;
  /** Number of bytes this unit occupies. */
  length: number;
  /** 1-based line, counting LF. A CR LF pair ends one line, not two. */
  line: number;
  /** 1-based column, counted in code points. */
  column: number;
}

export interface DecodeResult {
  units: Unit[];
  encoding: Encoding;
  /** Byte length of the byte-order mark, 0 when there is none. The BOM is NOT a unit. */
  bomLength: number;
  /** Number of bytes that could not be decoded. */
  invalidBytes: number;
  /** The text, reconstructed from the valid units. Invalid bytes appear as U+FFFD here only. */
  text: string;
}

const BOM_UTF8 = [0xef, 0xbb, 0xbf];
const BOM_UTF16LE = [0xff, 0xfe];
const BOM_UTF16BE = [0xfe, 0xff];

function startsWith(bytes: Uint8Array, sig: number[]): boolean {
  if (bytes.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) if (bytes[i] !== sig[i]) return false;
  return true;
}

/**
 * Which encoding is this?
 *
 * BOM first, because a BOM is a statement of fact rather than a guess. Without one, UTF-8 — the
 * only defensible default, and the one that keeps the byte splice exact for the overwhelming
 * majority of real files. A BOM-less UTF-16 file will decode as garbage, and charsight says so via
 * the invalid-byte count rather than pretending.
 *
 * UTF-16LE's BOM (FF FE) is a prefix of UTF-32LE's (FF FE 00 00). UTF-32 is vanishingly rare and is
 * not supported; the check below at least avoids claiming a UTF-32 file is UTF-16.
 */
export function sniffEncoding(bytes: Uint8Array): { encoding: Encoding; bomLength: number } {
  if (startsWith(bytes, BOM_UTF8)) return { encoding: 'utf-8', bomLength: 3 };
  if (startsWith(bytes, BOM_UTF16BE)) return { encoding: 'utf-16be', bomLength: 2 };
  if (startsWith(bytes, BOM_UTF16LE)) {
    if (bytes.length >= 4 && bytes[2] === 0x00 && bytes[3] === 0x00) {
      // UTF-32LE. Not supported; fall through to UTF-8 so the invalid-byte count tells the truth.
      return { encoding: 'utf-8', bomLength: 0 };
    }
    return { encoding: 'utf-16le', bomLength: 2 };
  }
  return { encoding: 'utf-8', bomLength: 0 };
}

/**
 * How many bytes does a valid UTF-8 sequence starting at `i` occupy, and what does it decode to?
 *
 * Strict per RFC 3629: no overlong forms, no surrogates, nothing above U+10FFFF. An overlong
 * encoding of '/' as C0 AF is a classic path-traversal trick and must never decode to '/'.
 */
function decodeUtf8At(b: Uint8Array, i: number): { cp: number; length: number } | null {
  const b0 = b[i]!;
  if (b0 < 0x80) return { cp: b0, length: 1 };

  const cont = (j: number, lo = 0x80, hi = 0xbf): boolean => {
    const x = b[j];
    return x !== undefined && x >= lo && x <= hi;
  };

  if (b0 >= 0xc2 && b0 <= 0xdf) {
    if (!cont(i + 1)) return null;
    return { cp: ((b0 & 0x1f) << 6) | (b[i + 1]! & 0x3f), length: 2 };
  }
  if (b0 >= 0xe0 && b0 <= 0xef) {
    const lo = b0 === 0xe0 ? 0xa0 : 0x80;
    const hi = b0 === 0xed ? 0x9f : 0xbf; // U+D800..U+DFFF are not characters
    if (!cont(i + 1, lo, hi) || !cont(i + 2)) return null;
    return {
      cp: ((b0 & 0x0f) << 12) | ((b[i + 1]! & 0x3f) << 6) | (b[i + 2]! & 0x3f),
      length: 3,
    };
  }
  if (b0 >= 0xf0 && b0 <= 0xf4) {
    const lo = b0 === 0xf0 ? 0x90 : 0x80;
    const hi = b0 === 0xf4 ? 0x8f : 0xbf; // nothing above U+10FFFF
    if (!cont(i + 1, lo, hi) || !cont(i + 2) || !cont(i + 3)) return null;
    return {
      cp:
        ((b0 & 0x07) << 18) |
        ((b[i + 1]! & 0x3f) << 12) |
        ((b[i + 2]! & 0x3f) << 6) |
        (b[i + 3]! & 0x3f),
      length: 4,
    };
  }
  return null;
}

/** Decode a buffer to code points, each carrying its exact byte span, line and column. */
export function decode(bytes: Uint8Array, forced?: Encoding): DecodeResult {
  const sniffed = sniffEncoding(bytes);
  const encoding = forced ?? sniffed.encoding;
  const bomLength = forced && forced !== sniffed.encoding ? 0 : sniffed.bomLength;

  const units: Unit[] = [];
  const pieces: string[] = [];
  let invalidBytes = 0;
  let line = 1;
  let column = 1;
  // A CR LF pair must advance the line once, not twice; a lone CR (old Mac line endings, and what
  // a truncated CRLF file leaves behind) still ends a line.
  let lastWasCR = false;

  const push = (cp: number, offset: number, length: number): void => {
    if (cp === 0x0a && lastWasCR) {
      // The LF of a CRLF pair: it belongs to the line that CR already ended.
      units.push({ cp, offset, length, line, column });
      lastWasCR = false;
      return;
    }
    units.push({ cp, offset, length, line, column });
    if (cp === 0x0a || cp === 0x0d) {
      line++;
      column = 1;
      lastWasCR = cp === 0x0d;
    } else {
      column++;
      lastWasCR = false;
    }
  };

  if (encoding === 'utf-8') {
    let i = bomLength;
    while (i < bytes.length) {
      const d = decodeUtf8At(bytes, i);
      if (d) {
        push(d.cp, i, d.length);
        pieces.push(String.fromCodePoint(d.cp));
        i += d.length;
      } else {
        push(-1, i, 1);
        pieces.push('�');
        invalidBytes++;
        i += 1;
      }
    }
  } else {
    const le = encoding === 'utf-16le';
    let i = bomLength;
    while (i + 1 < bytes.length) {
      const u = le ? bytes[i]! | (bytes[i + 1]! << 8) : (bytes[i]! << 8) | bytes[i + 1]!;
      if (u >= 0xd800 && u <= 0xdbff && i + 3 < bytes.length) {
        const v = le ? bytes[i + 2]! | (bytes[i + 3]! << 8) : (bytes[i + 2]! << 8) | bytes[i + 3]!;
        if (v >= 0xdc00 && v <= 0xdfff) {
          const cp = 0x10000 + ((u - 0xd800) << 10) + (v - 0xdc00);
          push(cp, i, 4);
          pieces.push(String.fromCodePoint(cp));
          i += 4;
          continue;
        }
      }
      if (u >= 0xd800 && u <= 0xdfff) {
        // An unpaired surrogate is not a character. Report it rather than decode it.
        push(-1, i, 2);
        pieces.push('�');
        invalidBytes += 2;
        i += 2;
        continue;
      }
      push(u, i, 2);
      pieces.push(String.fromCodePoint(u));
      i += 2;
    }
    if (i < bytes.length) {
      push(-1, i, bytes.length - i);
      pieces.push('�');
      invalidBytes += bytes.length - i;
    }
  }

  return { units, encoding, bomLength, invalidBytes, text: pieces.join('') };
}

/** Encode a code point back to bytes in the given encoding, for a replacement splice. */
export function encodeCodePoint(cp: number, encoding: Encoding): Uint8Array {
  if (encoding === 'utf-8') {
    if (cp < 0x80) return new Uint8Array([cp]);
    if (cp < 0x800) return new Uint8Array([0xc0 | (cp >> 6), 0x80 | (cp & 0x3f)]);
    if (cp < 0x10000) {
      return new Uint8Array([0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f)]);
    }
    return new Uint8Array([
      0xf0 | (cp >> 18),
      0x80 | ((cp >> 12) & 0x3f),
      0x80 | ((cp >> 6) & 0x3f),
      0x80 | (cp & 0x3f),
    ]);
  }
  const le = encoding === 'utf-16le';
  const put = (u: number, out: number[]): void => {
    if (le) out.push(u & 0xff, u >> 8);
    else out.push(u >> 8, u & 0xff);
  };
  const out: number[] = [];
  if (cp < 0x10000) put(cp, out);
  else {
    const x = cp - 0x10000;
    put(0xd800 + (x >> 10), out);
    put(0xdc00 + (x & 0x3ff), out);
  }
  return new Uint8Array(out);
}

/** Encode a whole string for a replacement splice. */
export function encodeString(s: string, encoding: Encoding): Uint8Array {
  const parts: Uint8Array[] = [];
  let total = 0;
  for (const ch of s) {
    const b = encodeCodePoint(ch.codePointAt(0)!, encoding);
    parts.push(b);
    total += b.length;
  }
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}
